import { useState } from "react";
import CnpjUtils from "../../../shared/utils/CnpjUtils";
import DateUtils from "../../../shared/utils/DateUtils";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";

export function useSimulations() {
	const [simulations, setSimulations] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});

	const validateDates = (startDate, endDate) => {
		const dateErrors = {};
		let message = null;

		const startDateInvalid = DateUtils.isDateEmptyOrInvalid(startDate);
		const endDateInvalid = DateUtils.isDateEmptyOrInvalid(endDate);

		if (startDateInvalid) {
			dateErrors.startDate = "Data de início obrigatória.";
		}

		if (endDateInvalid) {
			dateErrors.endDate = "Data final obrigatória.";
		}

		if (startDateInvalid && endDateInvalid) {
			message = "As datas de início e final são obrigatórias.";
		} else if (startDateInvalid) {
			message = "A data de início é obrigatória.";
		} else if (endDateInvalid) {
			message = "A data final é obrigatória.";
		}

		if (message) {
			return {
				errors: dateErrors,
				message
			};
		}

		const initialDate = DateUtils.normalizeDateOnly(startDate);
		const finalDate = DateUtils.normalizeDateOnly(endDate);

		if (initialDate > finalDate) {
			const invalidDateRangeMessage = "A data de início não pode ser maior que a data final.";

			dateErrors.startDate = invalidDateRangeMessage;
			dateErrors.endDate = invalidDateRangeMessage;

			return {
				errors: dateErrors,
				message: invalidDateRangeMessage
			};
		}

		return {
			errors: {},
			message: null
		};
	};

	const validateClientDocument = clientDocument => {
		const documentErrors = {};
		let message = null;

		if (!clientDocument) {
			return {
				errors: {},
				message: null
			};
		}

		const cleanClientDocument = CnpjUtils.cleanAlphanumericCnpj(clientDocument);

		if (cleanClientDocument.length !== 14) {
			documentErrors.clientDocument = "Informe o CNPJ completo.";

			message = "Informe o CNPJ completo antes de buscar.";
		}

		return {
			errors: documentErrors,
			message
		};
	};

	const validateClientName = clientName => {
		const nameErrors = {};
		let message = null;

		if (!clientName) {
			return {
				errors: {},
				message: null
			};
		}

		if (clientName.length < 5) {
			nameErrors.clientName = "Informe pelo menos 5 caracteres para buscar.";

			message = "Informe pelo menos 5 caracteres para buscar.";
		}

		return {
			errors: nameErrors,
			message
		};
	};

	const validateSimulationFilters = filters => {
		const dateValidation = validateDates(
			filters.startDate,
			filters.endDate
		);

		const clientDocumentValidation = validateClientDocument(
			filters.clientDocument
		);

		const clientNameValidation = validateClientName(
			filters.clientName
		);

		const errors = {
			...dateValidation.errors,
			...clientDocumentValidation.errors,
			...clientNameValidation.errors
		};

		const message =
			dateValidation.message ||
			clientDocumentValidation.message ||
			clientNameValidation.message;

		setValidationErrors(errors);

		return {
			errors,
			message
		};
	};

	const buildSimulationParams = (filters = {}, pagination = {}) => {
		const {
			page,
			size
		} = pagination;

		const formattedFilters = {
			...filters,
			startDate: DateUtils.formatDateToAmerican(filters.startDate),
			endDate: DateUtils.formatDateToAmerican(filters.endDate)
		};

		const params = new URLSearchParams();

		if (formattedFilters.clientDocument) {
			params.append(
				"cnpj",
				CnpjUtils.cleanAlphanumericCnpj(formattedFilters.clientDocument)
			);
		}

		if (!formattedFilters.clientDocument && formattedFilters.clientName) {
			params.append("nomeCliente", String(formattedFilters.clientName).toUpperCase());
		}

		if (formattedFilters.startDate) {
			params.append("dataInicio", formattedFilters.startDate);
		}

		if (formattedFilters.endDate) {
			params.append("dataFim", formattedFilters.endDate);
		}

		if (formattedFilters.status && formattedFilters.status !== "all") {
			params.append("status", String(formattedFilters.status).toUpperCase());
		}


		if (page !== undefined && page !== null) {
			params.append("page", page);
		}

		if (size !== undefined && size !== null) {
			params.append("size", size);
		}

		return params;
	};

	async function fetchSimulations(filters = {}, pagination = {}) {
		try {
			const {
				accessToken,
				clientId
			} = getAuthParams();

			const params = buildSimulationParams(filters, pagination);

			const requestOptions = {
				method: "GET",
				headers: {
					"Content-Type": "application/json",
					"access_token": accessToken,
					"client_id": clientId
				}
			};
			
			const response = await fetch(
				process.env.REACT_APP_ESSILOR_API + `pacto-rebates-simulador/v1/simulacoes?${params.toString()}`,
				requestOptions
			);

			if (!response.ok) {
				await throwResponseError(
					response,
					"Erro ao buscar simulações realizadas."
				);
			}

			const data = await response.json();

			const simulationsData = data.resultado || [];
			
			const responsePagination = {
				totalElements: data.totalElementos ?? 0,
				totalPages: data.totalPaginas ?? 0,
				page: data.pagina ?? 0,
				size: data.tamanho ?? pagination.size ?? 10
			};

			if (!Array.isArray(simulationsData) || simulationsData.length === 0) {
				setSimulations([]);

				return {
					ok: true,
					empty: true,
					simulations: [],
					pagination: responsePagination
				};
			}

			const formattedSimulations = simulationsData.map(simulation => ({
				id: simulation.idSimulacao,
				client: simulation.nomeCliente,
				cnpj: simulation.cnpj,
				date: simulation.dataInicioSimulacao,
				totalValue: simulation.valorSimulado,
				equipmentName: simulation.nomeEquipamentoSimulado,
				status: simulation.status,
				commercialCondition: simulation.nomeCondicaoComercial
			}));

			setSimulations(formattedSimulations);

			return {
				ok: true,
				empty: false,
				simulations: formattedSimulations,
				pagination: responsePagination
			};
		} catch (requestError) {
			setSimulations([]);

			return {
				ok: false,
				reason: "request",
				error: requestError.message
			};
		}
	}

	return {
		simulations,
		validationErrors,
		validateSimulationFilters,
		refetch: fetchSimulations
	};
}